import { match, P } from 'ts-pattern';
import { newFileId } from '~/utils/constants';
import { useExport, useFile, useFiles, usePathHistory } from '~/utils/hooks/hooks';
import { parseExport } from '~/utils/parseExport';
import { useStore } from '~/utils/store';
import {
  getId,
  getIndexPage,
  hasFolderLoadedByDepth,
  inlineEditingEnabled,
  isAFolder,
  isAPDF,
  isAPowerpoint,
  isASlide,
  isEmpty,
  isPageExport,
  macros,
  shouldShowEditButton,
} from '~/utils/utils';

type Props = {
  wikiId: string;
  id?: string | undefined;
};

type FolderStatus = 'loading' | 'new' | 'empty' | 'emptyPage' | 'hasChildren' | 'hasTreeMacro' | 'ready';
type PageStatus = 'loading' | 'new' | 'empty' | 'ready' | 'error' | 'editing';

export const usePageState = ({ wikiId, id }: Props) => {
  const swrFiles = useFiles(wikiId);
  const swrFile = useFile(id, swrFiles.files, wikiId);
  const { data: wiki } = useFile(wikiId, [], wikiId);
  const newFile = useStore((state) => state.newFile);
  const editing = useStore((state) => state.editing);
  const setEditing = useStore((state) => state.setEditing);
  const { previous, current } = usePathHistory();
  const file = swrFile.data;
  const { data: allFiles = [], files } = swrFiles;
  const isFolder = isAFolder(file);
  // react-compiler: fix first load for index page, otherwise allFiles does not update for getIndexPage
  // https://playground.react.dev/#N4Igzg9grgTgxgUxALhAgHgBwjALgAjggDswCowEAFAQwHMEBlXG3BfAXnwAph8B3AJYBrQQEkAJgBp8gifgC+yfFRgRMYAJScAfPmAAdYvkIky+-BNY1lNADZ2AYoLsIwnfAG0AujIBmLm6KHhQIzq5g3EKikpoA3EYmRKQEfFYsygGuwVyh4Qjccv6BYDLR4hLxiaYpFq7EdLgAFjn49k4lCcb4MAi4sMaCYI4QdhIIMPgAZFP49Y0tegAM+AD8+Ay4YsTj6LQM3FkIMu35WvjKQyNjE2v4UDsIAcQI8pmBXQpdIFIgyQF0FAgQQAW2weHwuAAnph2HwqHYoHRBMQAPKYXCCMzBPxqEH4ADkACMaESEHYALSYRHI4gU3o0OC4ClEMGBGAAegkQ1wBK6Rl41VZmBcrCxxAAshBxsoDCB2nKjAp8GAxWAAkEEUiUejMWZ4j9wE0IPxtmwYMR7GAUH4rQgFEA
  const { length } = allFiles;
  const page = isFolder && length > 0 ? getIndexPage(file, allFiles) : isFolder ? undefined : file;
  const swrExport = useExport(page ?? file);
  const children = isFolder ? files.filter((o) => o.parents?.[0] === getId(file)) : [];
  const { html, markdown } = swrExport.data ?? {};
  const { content } = parseExport({ html, markdown, file, files, wiki });
  const isEmptyPage = isPageExport(file) ? (swrExport.data ? isEmpty(content) : true) : false;
  const isNewFile = id?.startsWith(newFileId) || current.split('/').at(-1)?.startsWith(newFileId);
  const isNewlyCreated = (previous?.split('/').at(-1)?.startsWith(newFileId) || isNewFile) && files.at(-1)?.id === id;

  const isLoadingPage = match({ file, swrFile, page, swrExport })
    .with({ file: P.nullish, swrFile: { isLoading: true } }, () => true)
    .with({ swrExport: { data: P.union(P.nullish, { html: P.nullish }), isLoading: true } }, ({ page, file }) =>
      isPageExport(page ?? file)
    )
    .otherwise(() => false);

  const pageStatus = match({ swrExport, swrFile, file, id, isLoadingPage, isNewFile, isNewlyCreated, current, editing })
    .returnType<PageStatus>()
    .with({ editing: true }, () => 'editing')
    .with({ file: P.when(isASlide) }, () => 'ready')
    .with({ file: P.when(isAPowerpoint) }, () => 'ready')
    .with(
      P.union(
        { id: P.when((id) => id?.startsWith(newFileId)), swrExport: { isLoading: true } },
        { isLoadingPage: true },
        { isNewFile: true },
        { swrExport: { isLoading: true } },
        { swrFile: { data: P.when(isAPDF) }, swrExport: { data: P.union(P.nullish, { url: P.nullish }) } },
        { isNewFile: true }
      ),
      () => 'loading'
    )
    .with({ isNewlyCreated: true, swrExport: { data: P.nullish } }, () => 'new')
    .with(
      {
        swrFile: { data: P.when((file) => file && shouldShowEditButton(file)) },
        swrExport: { data: P.when((data) => isEmpty(data?.html)) },
      },
      () => 'empty'
    )
    .with(
      { file: P.nonNullable, swrFile: { isLoading: false, isValidating: false, error: P.nonNullable } },
      () => 'error'
    )
    .otherwise(() => 'ready');

  const folderLevelLoaded =
    children.length > 0 ||
    !swrFiles.isLoading ||
    hasFolderLoadedByDepth(file, files) ||
    (!isFolder && files.some((o) => o.id === id));

  const folderStatus = match({
    children,
    page,
    wiki,
    content,
    isLoadingPage,
    folderLevelLoaded,
    isNewlyCreated,
    isEmptyPage,
    file,
  })
    .returnType<FolderStatus>()
    .with(P.union({ isLoadingPage: true }, { folderLevelLoaded: false }), () => 'loading')
    .with({ isNewlyCreated: true, children: P.when((c) => c.length === 0) }, () => 'new')
    .with(
      {
        content: P.when((c) => c && macros.test.files.test(c)),
        wiki: P.nonNullable,
        page: P.nonNullable,
      },
      () => 'hasTreeMacro'
    )
    .with({ wiki: P.nonNullable, children: P.when((c) => c.length > 0), page: P.nullish }, () => 'hasChildren')
    .with({ page: P.nonNullable, isEmptyPage: true, isLoadingPage: false }, () => 'emptyPage')
    .with(
      {
        children: P.when((c) => c.length === 0),
        page: P.nullish,
        file: P.when((f) => f?.id !== wiki?.id),
        isNewlyCreated: false,
      },
      () => 'empty'
    )
    .otherwise(() => 'ready');

  const showInlineEditing = inlineEditingEnabled(wiki, file);

  return {
    state: {
      folder: {
        status: folderStatus,
      },
      page: {
        status: pageStatus,
        showLastModified: Boolean(file) && wiki?.properties?.showLastModified !== 'false',
        showInlineEditing,
        editing,
        setEditing,
      },
      wiki: {
        isEmpty:
          isFolder && file?.id === wiki?.id && children.length === 0 && folderLevelLoaded && !isLoadingPage && !page,
      },
      all: {
        isError: !file && !swrFile.isLoading && swrFile.error,
        isLoading: !file || (swrFiles.isLoading && allFiles.length === 0),
        isNew: newFile?.id === id,
      },
    },
    data: {
      swrFile,
      swrFiles,
      swrExport,
      content,
      page,
      children,
      folderLevelLoaded,
    },
  };
};
